<template>
    <CollectionLayout
        :is-collection-partner-visma="props.isCollectionPartnerVisma"
    >
        <DCard padding>
            <div class="collection-filter">
                <DCol>
                    <DRow
                        class="mb-3"
                        align-vertical="end"
                    >
                        <DCol
                            v-if="props.statuses.length > 0"
                            sm="5"
                        >
                            <DMultiSelect
                                id="status-dropdown"
                                :options="statusOptions"
                                :search="true"
                                :selection="true"
                                :options-empty-placeholder="__('products.productGroups.form.no_options_found')"
                                selection-type="text"
                                :label="__('contracts.contracts.status')"
                                @update="updateSelectedStatus"
                            />
                        </DCol>
                        <DCol
                            sm="3"
                            class="d-flex gap-2"
                        >
                            <DButton
                                v-if="userPermissions.includes('collection.create')"
                                id="filter-apply"
                                :label="__('tools.collection.filters.filter')"
                                @click="changePage"
                            />
                            <DButton
                                id="filter-reset"
                                icon="filter-off"
                                :label="__('messages.actions.reset_filter')"
                                @click="resetFilters"
                            />
                        </DCol>
                    </DRow>
                </DCol>
            </div>
            <DTable
                :key="tableKey"
                :items="data"
                :fields="fields"
            >
                <template #status="{item}">
                    <td v-if="item.status === null">
                        N/A
                    </td>
                    <td v-else>
                        <DTag
                            :label="__('tools.batches.statuses.' + item.status)"
                            :color="getStatusTagColor(item.status)"
                        />
                    </td>
                </template>
                <template #actions="{item}">
                    <td>
                        <div style="display: flex; grid-gap: 10px">
                            <DButton
                                v-if="item.link !== null && !props.isCollectionPartnerVisma"
                                icon="download"
                                mini
                                @click="downloadCsv(item)"
                            />
                            <DButton
                                icon="pencil"
                                mini
                                @click="openModal(item.id)"
                            />
                            <DButton
                                icon="eye"
                                mini
                                @click="show(item.id)"
                            />
                        </div>
                    </td>
                </template>
            </DTable>
            <DPagination
                v-if="batches.last_page >= 1"
                :active-page="batches.current_page"
                :pages="batches.last_page"
                align="center"
                @update:activePage="changePage"
            />
        </DCard>
        <DModal
            is-confirm
            is-medium
            :title="__('tools.batches.modal.title')"
            :message="__('tools.batches.modal.message')"
            :show="showModal"
            @confirm="changeToDone"
            @close="closeModal"
        />
    </CollectionLayout>
</template>

<script setup>

import { router, usePage } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'
import { computed, onMounted, ref } from 'vue'
import { route } from '~@/Composables/route'
import CollectionLayout from '~@/Pages/Tools/Collection/Layout.vue'

const props = defineProps({
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    batches: {
        type: Object,
        default: () => {
            return {}
        }
    },
    statuses: {
        type: Object,
        default: () => {
            return {}
        }
    },
    status: {
        type: Array,
        default: () => {
            return []
        }
    },
    isCollectionPartnerVisma: {
        type: Boolean,
        default: false
    }
})

const fields = [
    { key: 'id', label: __('tools.batches.id') },
    { key: 'startDate', label: __('tools.batches.start_date') },
    { key: 'numberOfItems', label: __('tools.batches.number_of_items') },
    { key: 'numberOfFailedItems', label: __('tools.batches.number_of_failed_items') },
    { key: 'status', label: __('tools.batches.status') },
    { key: 'actions', label: '', _style: { minWidth: '5%' } }
]

const userPermissions = computed(() => usePage().props.auth.permissions)
const show = (id) => router.get(route('tools.collection.show', { batch: id }))

const downloadCsv = (item) => {
    const link = document.createElement('a')
    link.href = route('tools.collection.download-batch-csv', { batch: item })
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

const statusOptions = computed(() => {
    return props.statuses.map((item) => {
        return {
            value: item.value,
            text: __(`tools.batches.statuses.${item.value}`),
            selected: status.value.includes(item.value)
        }
    })
})

const tableKey = ref(0)
const data = ref([])
const status = ref(props.status)
const updateSelectedStatus = (values) => {
    status.value = values
}

const filter = {
    status: null
}
const resetFilters = () => {
    status.value = []
    changePage(1, true)
}
const changePage = (page, defaultFilters = false) => {
    filter.status = Object.values(status.value)
    const query = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }
    router.get(route('tools.collection.batches', {
        saveFilters: true,
        defaultFilters: defaultFilters
    }), query)
}

onMounted(() => {
    window.laravelEcho.channel('onecrm.collection.batches').listen('.collection.batch_status_updated', socketEvent => {
        handleBatchUpdate(socketEvent)
    })
    data.value = props.items
})

const showModal = ref(false)
let itemId = null
const openModal = (id) => {
    itemId = id
    showModal.value = true
}
const closeModal = () => {
    showModal.value = false
}
const changeToDone = () => router.get(route('tools.collection.change-to-done', { batch: itemId }))
const handleBatchUpdate = (socketEvent) => {
    // do something only if event came from same tenant
    if (socketEvent.tenant !== usePage().props.tenant) {
        return
    }

    const batch = socketEvent.batch
    // find the batch in the table list
    const existingBatchId = data.value.findIndex(existingBatch => existingBatch.id === batch.id)

    // remove highlight-row class from selected row after 3000ms
    setTimeout(() => {
        batch._classes = ''

        data.value.splice(
            data.value.findIndex(existingBatch => existingBatch.id === batch.id),
            1,
            batch
        )
        tableKey.value++
    }, 3000)

    // highlight the table row of the updated/new batch
    batch._classes = batch.status === 'failed' ? 'highlight-row' : 'highlight-row-success'

    // if batch doesn't already exist in the list, add it to the end of the list
    if (existingBatchId === -1) {
        data.value.push(batch)
    } else {
        data.value.splice(existingBatchId, 1, batch)
    }
}

const getStatusTagColor = (status) => {
    if (status === 'in_progress') {
        return 'primary'
    }
    if (status === 'ready_for_download') {
        return 'secondary'
    }
    if (status === 'done') {
        return 'success'
    }
    if (status === 'failed') {
        return 'danger'
    }
    return 'danger'
}
</script>

<style lang="scss" scoped>
.header-actions {
    display: grid;
    justify-items: flex-start;
    gap: 10px;
}
</style>
